import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportService } from '../services/api';
import ReportFiltersSection from '../components/ReportFiltersSection';
import ReportMetricsSection from '../components/ReportMetricsSection';
import ReportPreviewSection from '../components/ReportPreviewSection';
import styles from './CreateReportPage.module.css';

const DEFAULT_FILTERS = {
    governorates: [],
    from_date: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    to_date: new Date().toISOString().split('T')[0],
    protest_type: [],
    verified: null,
    with_sources: false,
};

const DEFAULT_METRICS = {
    total_protests:           true,
    total_participants:       true,
    avg_participants:         true,
    top_governorates:         true,
    political_orientation:    false,
    verified_ratio:           true,
    protest_type_distribution: false,
    tag_cloud:                true,
};

const DEFAULT_CHART_TYPES = {
    top_governorates:          'bar',
    political_orientation:     'pie',
    verified_ratio:            'pie',
    protest_type_distribution: 'bar',
    tag_cloud:                 'cloud',
};

const STEPS = ['الفلاتر', 'المؤشرات', 'المعاينة'];

export default function CreateReportPage() {
    const navigate = useNavigate();
    const [step,            setStep]           = useState(1);
    const [filters,         setFilters]        = useState(DEFAULT_FILTERS);
    const [selectedMetrics, setSelectedMetrics] = useState(DEFAULT_METRICS);
    const [chartTypes,      setChartTypes]     = useState(DEFAULT_CHART_TYPES);
    const [reportData,      setReportData]     = useState(null);
    const [protestsCount,   setProtestsCount]  = useState(null);
    const [loading,         setLoading]        = useState(false);
    const [saving,          setSaving]         = useState(false);
    const [error,           setError]          = useState('');

    const handlePreview = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await reportService.preview({
                ...filters,
                selected_metrics: selectedMetrics,
            });
            setReportData(res.data.report_data);
            setProtestsCount(res.data.protests_count);
            setStep(3);
        } catch (err) {
            setError(err.response?.data?.message || 'فشل تحميل بيانات التقرير');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!reportData) return;
        const title = window.prompt('اسم التقرير:');
        if (!title?.trim()) return;

        setSaving(true);
        setError('');
        try {
            await reportService.save({
                title: title.trim(),
                filters,
                selected_metrics: selectedMetrics,
                chart_types: chartTypes,
                report_data: reportData,
            });
            navigate('/reports');
        } catch (err) {
            setError(err.response?.data?.message || 'فشل حفظ التقرير');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={`${styles.page} text-ar`}>
            {/* Header */}
            <div className={styles.header}>
                <button className={styles.closeBtn} onClick={() => navigate('/reports')}>← العودة</button>
                <div>
                    <h1 className={styles.heading}>إنشاء تقرير جديد</h1>
                    <p className={styles.subheading}>اختر الفلاتر والمؤشرات التي تريد عرضها في التقرير</p>
                </div>
            </div>

            {/* Step indicator */}
            <div className={styles.stepper}>
                {STEPS.map((label, idx) => {
                    const n = idx + 1;
                    return (
                        <React.Fragment key={n}>
                            <div className={`${styles.stepItem} ${step === n ? styles.active : ''} ${step > n ? styles.done : ''}`}>
                                <span className={styles.stepNum}>{step > n ? '✓' : n}</span>
                                <span className={styles.stepLabel}>{label}</span>
                            </div>
                            {idx < STEPS.length - 1 && <div className={`${styles.stepLine} ${step > n ? styles.doneLine : ''}`} />}
                        </React.Fragment>
                    );
                })}
            </div>

            {error && <div className={styles.error}>{error}</div>}

            {/* Step content */}
            <div className={styles.card}>
                {step === 1 && (
                    <ReportFiltersSection
                        filters={filters}
                        onChange={setFilters}
                        onNext={() => setStep(2)}
                    />
                )}
                {step === 2 && (
                    <ReportMetricsSection
                        selectedMetrics={selectedMetrics}
                        chartTypes={chartTypes}
                        onMetricsChange={setSelectedMetrics}
                        onChartTypesChange={setChartTypes}
                        onNext={handlePreview}
                        onBack={() => setStep(1)}
                        loading={loading}
                    />
                )}
                {step === 3 && reportData && (
                    <ReportPreviewSection
                        reportData={reportData}
                        selectedMetrics={selectedMetrics}
                        chartTypes={chartTypes}
                        protestsCount={protestsCount}
                        onSave={handleSave}
                        onBack={() => setStep(2)}
                        saving={saving}
                    />
                )}
            </div>
        </div>
    );
}
